import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, Star, X, ArrowLeft } from 'lucide-react';
import { useBinancePairsTicker } from '../hooks/useBinancePairsTicker';
import { useTwelveDataPairsTicker } from '../hooks/useTwelveDataPairsTicker';
import toast from 'react-hot-toast';

const FAVORITES_STORAGE_KEY = 'sportsdey.favoritePairs';
const MARKET_TYPE_OPTIONS = [
  { value: 'crypto', label: 'Crypto' },
  { value: 'future', label: 'Futures' },
  { value: 'forex', label: 'Forex' },
  { value: 'commodities', label: 'Commodities' }
];

export function BinancePairsModal({
  open,
  onOpenChange,
  pairs,
  selectedPair,
  onSelectPair,
  anchorRef,
  title = 'Market Pairs',
  pairType = 'crypto',
  onPairTypeChange,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore
}) {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [favoriteSymbols, setFavoriteSymbols] = useState(() => loadFavorites());
  const [isClosing, setIsClosing] = useState(false);
  const [isMounted, setIsMounted] = useState(open);

  const panelRef = useRef(null);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setIsMounted(true);
      setIsClosing(false);
    } else if (isMounted && !isClosing) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setIsClosing(false);
        setIsMounted(false);
      }, 220);
      return () => clearTimeout(timer);
    }
  }, [open, isMounted, isClosing]);

  const handleClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setIsMounted(false);
      onOpenChange(false);
    }, 220);
  };

  // Lock background scroll and handle Escape key
  useEffect(() => {
    if (!open && !isClosing) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, isClosing]);

  useEffect(() => {
    try {
      window.localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favoriteSymbols));
    } catch (_error) {
      // Ignore storage failures in restricted contexts
    }
  }, [favoriteSymbols]);

  const isBinanceType = pairType === 'crypto' || pairType === 'future';
  const binancePairs = useBinancePairsTicker(isBinanceType ? pairs : [], pairType);
  const twelveDataPairs = useTwelveDataPairsTicker(isBinanceType ? [] : pairs);
  const livePairs = isBinanceType ? binancePairs : twelveDataPairs;

  const filteredPairs = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return livePairs?.filter((pair) => {
      if (activeTab === 'favorites' && !favoriteSymbols.includes(pair.symbol)) {
        return false;
      }
      if (!normalizedQuery) {
        return true;
      }
      return (
        pair.symbol.toLowerCase().includes(normalizedQuery) ||
        pair.label?.toLowerCase().includes(normalizedQuery)
      );
    });
  }, [activeTab, favoriteSymbols, livePairs, searchQuery]);

  const handleListScroll = (event) => {
    if (!hasMore || isLoadingMore || typeof onLoadMore !== 'function') {
      return;
    }
    const listElement = event.currentTarget;
    const distanceToBottom = listElement.scrollHeight - listElement.scrollTop - listElement.clientHeight;
    if (distanceToBottom <= 100) {
      onLoadMore();
    }
  };

  useEffect(() => {
    if (!open || hasMore === false || isLoadingMore || typeof onLoadMore !== 'function') {
      return;
    }
    if (activeTab !== 'all' || searchQuery.trim().length > 0) {
      return;
    }
    const listElement = listRef.current;
    if (!listElement) {
      return;
    }
    if (listElement.scrollHeight <= listElement.clientHeight + 4) {
      onLoadMore();
    }
  }, [activeTab, hasMore, isLoadingMore, onLoadMore, open, pairs?.length, searchQuery]);

  if (!open && !isClosing) {
    return null;
  }

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className={`binance-pairs-fullscreen-root ${isClosing ? 'is-closing' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="binance-pairs-fullscreen-backdrop" onClick={handleClose} />

      <section
        ref={panelRef}
        className={`binance-pairs-fullscreen-panel ${isClosing ? 'is-closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Header */}
        <header className="binance-pairs-fullscreen-header">
          <button
            type="button"
            className="binance-pairs-fullscreen-back-btn"
            onClick={handleClose}
            aria-label="Back"
          >
            <ArrowLeft size={20} />
          </button>

          <h2 className="binance-pairs-fullscreen-title">{title}</h2>

          <button
            type="button"
            className="binance-pairs-fullscreen-close-btn"
            onClick={handleClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </header>

        {/* Search Bar */}
        <div className="binance-pairs-fullscreen-search">
          <Search size={16} className="binance-pairs-search-icon" aria-hidden="true" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search coin or pair (e.g. BTC, ETH)..."
            aria-label="Search pair"
          />
          {searchQuery ? (
            <button
              type="button"
              className="binance-pairs-search-clear"
              onClick={() => {
                setSearchQuery('');
                searchInputRef.current?.focus();
              }}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          ) : null}
        </div>

        {/* Market Category Pills */}
        <div className="binance-pairs-categories" role="tablist" aria-label="Market Categories">
          {MARKET_TYPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={pairType === option.value}
              className={`binance-pairs-category-pill ${pairType === option.value ? 'is-active' : ''}`}
              onClick={() => onPairTypeChange?.(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Filter Subtabs */}
        <div className="binance-pairs-subtabs">
          <button
            type="button"
            className={`binance-pairs-subtab ${activeTab === 'all' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            <span>All Pairs</span>
            <span className="binance-pairs-count-badge">{filteredPairs?.length || 0}</span>
          </button>
          <button
            type="button"
            className={`binance-pairs-subtab ${activeTab === 'favorites' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('favorites')}
          >
            <Star size={13} className={favoriteSymbols.length > 0 ? 'text-amber-400' : ''} />
            <span>Favorites</span>
            {favoriteSymbols.length > 0 && (
              <span className="binance-pairs-count-badge is-fav">{favoriteSymbols.length}</span>
            )}
          </button>
        </div>

        {/* Column Headers */}
        <div className="binance-pairs-columns-header" aria-hidden="true">
          <span className="col-symbol">Symbol</span>
          <span className="col-price">Last Price</span>
          <span className="col-change">24h Chg%</span>
        </div>

        {/* Pairs Scrollable List */}
        <div ref={listRef} className="binance-pairs-list-container" onScroll={handleListScroll}>
          {filteredPairs?.length === 0 && isLoadingMore ? (
            <div className="binance-pairs-feedback">
              <div className="binance-pairs-spinner" />
              <p>Loading pairs...</p>
            </div>
          ) : filteredPairs?.length === 0 ? (
            <div className="binance-pairs-feedback">
              <p className="binance-pairs-empty-text">No pairs found matching your filter</p>
            </div>
          ) : (
            <>
              {filteredPairs?.map((pair) => {
                const { baseAsset, quoteAsset } = getPairAssets(pair);
                const isFavorite = favoriteSymbols?.includes(pair.symbol);
                const changePercent = Number(pair.changePercent) ? Number(pair.changePercent) : 0;
                const changeTone = changePercent >= 0 ? 'is-up' : 'is-down';
                const spreadPercent = Number.isFinite(pair.spreadPercent) ? pair.spreadPercent : 0;
                const isHot = Math.abs(changePercent) >= 2 || spreadPercent >= 2;

                return (
                  <article
                    key={pair.symbol}
                    className={`binance-pairs-card-row ${selectedPair === pair.symbol ? 'is-active' : ''}`}
                    title={buildPairTooltip(pair)}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (pairType === 'forex' && pair?.symbol !== 'EURUSD') {
                        toast.error('Coming soon!');
                        return;
                      } else if (pairType === 'commodities' && pair?.symbol !== 'XAUUSD') {
                        toast.error('Coming soon!');
                        return;
                      }
                      onSelectPair?.(pair.symbol);
                      handleClose();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        if (pairType === 'forex' && pair?.symbol !== 'EURUSD') {
                          toast.error('Coming soon!');
                          return;
                        } else if (pairType === 'commodities' && pair?.symbol !== 'XAUUSD') {
                          toast.error('Coming soon!');
                          return;
                        }
                        onSelectPair?.(pair.symbol);
                        handleClose();
                      }
                    }}
                  >
                    {/* Left: Star + Icon + Symbol */}
                    <div className="binance-pairs-row-left">
                      <button
                        type="button"
                        className={`binance-pairs-favorite-btn ${isFavorite ? 'is-favorite' : ''}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          setFavoriteSymbols((previous) => toggleFavorite(previous, pair.symbol));
                        }}
                        aria-label={isFavorite ? `Remove from favorites` : `Add to favorites`}
                      >
                        <Star size={13} />
                      </button>

                      <CoinLogo baseAsset={baseAsset} icon={pair.icon} />

                      <div className="binance-pairs-symbol-group">
                        <span className="binance-pairs-base-name">
                          {baseAsset}
                          <span className="binance-pairs-quote-name">/{quoteAsset}</span>
                          {isHot ? <small className="binance-pairs-hot-badge">🔥</small> : null}
                        </span>
                        <span className="binance-pairs-pair-type">{pairType.toUpperCase()}</span>
                      </div>
                    </div>

                    {/* Center: Price */}
                    <div className="binance-pairs-row-center">
                      <span className="binance-pairs-price-value">
                        {formatTickerPrice(pair.price)}
                      </span>
                    </div>

                    {/* Right: Change % */}
                    <div className="binance-pairs-row-right">
                      <span className={`binance-pairs-change-pill ${changeTone}`}>
                        {formatSignedPercent(changePercent)}
                      </span>
                    </div>
                  </article>
                );
              })}

              {isLoadingMore && (
                <div className="binance-pairs-loading-more">
                  <div className="binance-pairs-spinner-small" />
                  <span>Loading more pairs...</span>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>,
    document.body
  );
}

function CoinLogo({ baseAsset, icon }) {
  const [hasError, setHasError] = useState(false);
  const symbol = baseAsset?.toLowerCase() || '';
  const iconSrc = `https://cdn.jsdelivr.net/gh/spothq/cryptocurrency-icons@master/128/color/${symbol}.png`;

  return (
    <span className="binance-pairs-coin-avatar">
      {!hasError ? (
        <img
          src={icon || iconSrc}
          alt={`${baseAsset} logo`}
          loading="lazy"
          decoding="async"
          onError={() => setHasError(true)}
        />
      ) : (
        <b>{baseAsset?.slice(0, 1) || '?'}</b>
      )}
    </span>
  );
}

function getPairAssets(pair) {
  const label = typeof pair?.label === 'string' ? pair.label.trim() : '';
  if (label.includes('/')) {
    const [baseAsset, quoteAsset] = label.split('/');
    return {
      baseAsset: baseAsset || '--',
      quoteAsset: quoteAsset || '--'
    };
  }

  const symbol = typeof pair?.symbol === 'string' ? pair.symbol : '';
  const knownQuotes = ['USDT', 'USDC', 'BUSD', 'FDUSD', 'BTC', 'ETH'];
  const quoteAsset = knownQuotes.find((quote) => symbol.endsWith(quote));
  if (!quoteAsset) {
    return {
      baseAsset: symbol || '--',
      quoteAsset: '--'
    };
  }

  return {
    baseAsset: symbol.slice(0, symbol.length - quoteAsset.length) || '--',
    quoteAsset
  };
}

function formatTickerPrice(value) {
  value = Number(value);
  if (!Number.isFinite(value)) {
    return '--';
  }

  if (value >= 1000) {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  if (value >= 1) {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4
    });
  }

  return value.toLocaleString('en-US', {
    minimumFractionDigits: 5,
    maximumFractionDigits: 5
  });
}

function formatSignedPercent(value) {
  const percent = Number(value);
  if (!Number.isFinite(percent)) {
    return '--';
  }
  const normalized = Math.abs(percent).toFixed(2);
  if (percent > 0) {
    return `+${normalized}%`;
  }
  if (percent < 0) {
    return `-${normalized}%`;
  }
  return `${normalized}%`;
}

function buildPairTooltip(pair) {
  const high = Number.isFinite(pair.highPrice) ? formatTickerPrice(pair.highPrice) : '--';
  const low = Number.isFinite(pair.lowPrice) ? formatTickerPrice(pair.lowPrice) : '--';
  const spread = Number.isFinite(pair.spread) ? formatTickerPrice(pair.spread) : '--';
  const direction = pair.direction === 'down' ? 'Down' : pair.direction === 'flat' ? 'Flat' : 'Up';
  return `High: ${high} | Low: ${low} | Spread: ${spread} | Up/Down: ${direction}`;
}

function loadFavorites() {
  try {
    const raw = window.localStorage.getItem(FAVORITES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((value) => typeof value === 'string') : [];
  } catch (_error) {
    return [];
  }
}

function toggleFavorite(previous, symbol) {
  if (previous.includes(symbol)) {
    return previous.filter((entry) => entry !== symbol);
  }
  return [...previous, symbol];
}
